import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import styles from './LessonPlanEditor.module.css';
import sharedMarkdown from '../styles/markdownShared.module.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import RichTextEditor from './RichTextEditor';
import { isAnswerLine, isDrawingSpace, extractTextFromChildren, safeUrlTransform, rehypePlugins } from '../utils/markdownComponents';

const markdownStyles = { ...styles, ...sharedMarkdown };

const ContentEditor = forwardRef(({ content, selectedField, onSectionSelect, selectedSectionForChat }, ref) => {
  const [rawMarkdown, setRawMarkdown] = useState(content || '');
  const [sections, setSections] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const sectionRefs = useRef([]);
  const richTextEditorRefs = useRef([]);
  const isSwitchingRef = useRef(false);

  useEffect(() => {
    splitMarkdownIntoSections(rawMarkdown);
  }, [rawMarkdown]);

  useEffect(() => {
    if (content !== rawMarkdown) {
      setRawMarkdown(content || '');
    }
  }, [content]);

  useEffect(() => {
    if (!selectedField) return;

    const targetIndex = sections.findIndex((section) =>
      section.startsWith(`## ${selectedField}`)
    );

    if (targetIndex !== -1 && sectionRefs.current[targetIndex]) {
      sectionRefs.current[targetIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setEditingIndex(null);
    }
  }, [selectedField, sections]);

  // Helper: flush the live editor content into sections before reading
  const getLatestSections = () => {
    const updated = [...sections];
    if (editingIndex !== null) {
      const editorRef = richTextEditorRefs.current[editingIndex];
      if (editorRef && editorRef.getContent) {
        const liveContent = editorRef.getContent();
        if (liveContent) {
          updated[editingIndex] = liveContent;
        }
      }
    }
    return updated;
  };

  useImperativeHandle(ref, () => ({
    saveAll: async () => {
      const latest = getLatestSections();
      // Persist into state
      setSections(latest);
      if (editingIndex !== null) {
        setEditingIndex(null); // exit editing mode
      }
      const finalMarkdown = latest.join('\n\n');
      setRawMarkdown(finalMarkdown);
      return finalMarkdown;
    },
    getMarkdown: () => {
      const latest = getLatestSections();
      return latest.join('\n\n');
    }
  }));

  const splitMarkdownIntoSections = (markdown) => {
    if (!markdown || typeof markdown !== "string") {
      setSections([]);
      return;
    }
  
    // 1. Remove wrapping quotes if present
    let cleaned = markdown.trim();
    if ((cleaned.startsWith('"') && cleaned.endsWith('"')) || 
        (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
      cleaned = cleaned.slice(1, -1);
    }
  
    // 2. Remove triple backtick code blocks
    cleaned = cleaned.replace(/```[\s\S]*?```/g, '');
  
    // 3. Clean lines: remove non-breaking spaces, trim left spaces
    cleaned = cleaned
      .split('\n')
      .map(line => line.replace(/\u00A0/g, '').trimStart())
      .join('\n');
  
    // 4. Match all H2 headings (## Heading)
    const sectionRegex = /^#{2}\s.+$/gm;
    const matches = [...cleaned.matchAll(sectionRegex)];
  
    // 5. Split into sections between matches
    const splitSections = [];
    for (let i = 0; i < matches.length; i++) {
      const start = matches[i].index;
      const end = i + 1 < matches.length ? matches[i + 1].index : cleaned.length;
      splitSections.push(cleaned.slice(start, end).trim());
    }
  
    // If no sections found, treat entire content as one section
    if (splitSections.length === 0 && cleaned.length > 0) {
      splitSections.push(cleaned);
    }
  
    setSections(splitSections);
  };

  const handleChange = (index, value) => {
    const updated = [...sections];
    updated[index] = value;
    setSections(updated);
  };

  const handleSectionSave = (index, content) => {
    handleChange(index, content);
    setEditingIndex(null);
  };

  const handleSectionCancel = () => {
    // Only cancel if we're not in the process of switching sections
    if (!isSwitchingRef.current) {
      setEditingIndex(null);
    }
  };

  const handleSectionEdit = (index) => {
    // If another section is being edited, save it first without closing
    if (editingIndex !== null && editingIndex !== index) {
      // Set flag to prevent blur handler from closing
      isSwitchingRef.current = true;
      
      // Get content from current editing section and save it
      const currentEditorRef = richTextEditorRefs.current[editingIndex];
      if (currentEditorRef && currentEditorRef.getContent) {
        const currentContent = currentEditorRef.getContent();
        if (currentContent) {
          // Save the content without closing (just update the section content)
          handleChange(editingIndex, currentContent);
        }
      }
      
      // Switch directly to the new section
      setEditingIndex(index);
      
      // Reset flag after a short delay to allow the new editor to mount
      setTimeout(() => {
        isSwitchingRef.current = false;
      }, 300);
    } else {
      setEditingIndex(index);
    }
  };

  const handleMoveSection = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === sections.length - 1) return;

    // If a section is currently being edited, save it first
    if (editingIndex !== null) {
      const currentEditorRef = richTextEditorRefs.current[editingIndex];
      if (currentEditorRef && currentEditorRef.getContent) {
        const currentContent = currentEditorRef.getContent();
        if (currentContent) {
          handleSectionSave(editingIndex, currentContent);
        }
      }
    }

    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap sections
    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    
    setSections(newSections);
    
    // Update editing index if needed
    if (editingIndex === index) {
      setEditingIndex(targetIndex);
    } else if (editingIndex === targetIndex) {
      setEditingIndex(index);
    } else if (editingIndex !== null) {
      // If another section was being edited, close it
      setEditingIndex(null);
    }
  };

  const handleDeleteSection = (index) => {
    if (sections.length <= 1) {
      alert('Cannot delete the last section. Add content instead.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this section?')) {
      // If a section is currently being edited, save it first
      if (editingIndex !== null) {
        const currentEditorRef = richTextEditorRefs.current[editingIndex];
        if (currentEditorRef && currentEditorRef.getContent) {
          const currentContent = currentEditorRef.getContent();
          if (currentContent) {
            handleSectionSave(editingIndex, currentContent);
          }
        }
      }

      const newSections = sections.filter((_, i) => i !== index);
      setSections(newSections);
      
      if (editingIndex === index) {
        handleSectionCancel();
      } else if (editingIndex > index) {
        setEditingIndex(editingIndex - 1);
      } else if (editingIndex !== null) {
        // If another section was being edited, close it
        setEditingIndex(null);
      }
    }
  };

  const handleSectionClick = (section) => {
    if (onSectionSelect) {
      const sectionName = section.match(/^##\s*(.*)/)?.[1];
      if (sectionName) {
        // Toggle selection: if already selected, deselect it
        if (selectedSectionForChat === sectionName) {
          onSectionSelect(null);
        } else {
          onSectionSelect(sectionName);
        }
      }
    }
  };

  const stripLeadingSectionHeading = (sectionMarkdown, sectionName) => {
    if (!sectionMarkdown || !sectionName) return sectionMarkdown;
    const lines = sectionMarkdown.split('\n');
    const firstLine = (lines[0] || '').trim();
    if (firstLine === `## ${sectionName}`) {
      return lines.slice(1).join('\n').replace(/^\s+/, '');
    }
    return sectionMarkdown;
  };

  return (
    <div className={styles.editorContainer}>
      {sections.map((section, index) => {
        const isEditing = editingIndex === index;
        const sectionName = section.match(/^##\s*(.*)/)?.[1];
        const sectionContentForDisplay = stripLeadingSectionHeading(section, sectionName);
        const isSelectedForChat = selectedSectionForChat === sectionName;
        // Generate ID for scrolling
        const sectionId = sectionName ? sectionName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : `section-${index}`;
        
        return (
          <div
            key={index}
            id={sectionId}
            ref={(el) => (sectionRefs.current[index] = el)}
            className={`${styles.section} ${isEditing ? styles.sectionEditing : ''} ${isSelectedForChat ? styles.sectionSelectedForChat : ''}`}
            onClick={() => handleSectionClick(section)}
          >
            {/* Section Header */}
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                {section.match(/^##\s*(.*)/)?.[1] || `Section ${index + 1}`}
              </h2>
              
              {/* Section Controls */}
              <div className={styles.sectionControls}>
                {isEditing && (
                  <button
                    onClick={() => {
                      const editorRef = richTextEditorRefs.current[index];
                      if (editorRef && editorRef.getContent) {
                        const content = editorRef.getContent();
                        if (content) {
                          handleSectionSave(index, content);
                        }
                      }
                    }}
                    className={`${styles.controlButton} ${styles.saveButton}`}
                    title="Save Section"
                  >
                    💾 Save
                  </button>
                )}
                {/* Move buttons are always visible, even when editing */}
                <button
                  onClick={() => handleMoveSection(index, 'up')}
                  className={styles.controlButton}
                  title="Move Up"
                  disabled={index === 0}
                >
                  ⬆️
                </button>
                <button
                  onClick={() => handleMoveSection(index, 'down')}
                  className={styles.controlButton}
                  title="Move Down"
                  disabled={index === sections.length - 1}
                >
                  ⬇️
                </button>
                {!isEditing && (
                  <button
                    onClick={() => handleDeleteSection(index)}
                    className={`${styles.controlButton} ${styles.deleteButton}`}
                    title="Delete Section"
                    disabled={sections.length <= 1}
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>

            {/* Section Content */}
            <div className={styles.sectionContent}>
              {isEditing ? (
                <RichTextEditor
                  ref={(el) => {
                    if (el) {
                      richTextEditorRefs.current[index] = el;
                    }
                  }}
                  content={section}
                  onSave={(content) => handleSectionSave(index, content)}
                  onCancel={handleSectionCancel}
                  showCreateTable={true}
                />
              ) : (
                <div 
                  className={styles.sectionDisplay}
                  onClick={() => {
                    handleSectionEdit(index);
                  }}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={rehypePlugins}
                    urlTransform={safeUrlTransform}
                    components={{
                      table: ({ node, ...props }) => (
                        <table {...props} className={styles.customTable} />
                      ),
                      th: ({ node, ...props }) => (
                        <th {...props} className={styles.tableHeaderCell} />
                      ),
                      td: ({ node, ...props }) => (
                        <td {...props} className={styles.tableCell} />
                      ),
                      h2: ({ node, children, ...props }) => (
                        <h2 {...props} className={styles.markdownH2}>{children}</h2>
                      ),
                      h3: ({ node, children, ...props }) => (
                        <h3 {...props} className={styles.markdownH3}>{children}</h3>
                      ),
                      p: ({ node, children, ...props }) => {
                        const text = extractTextFromChildren(children);
                        if (isDrawingSpace(text)) {
                          return <div className={markdownStyles.drawingSpace} aria-label="Space for student drawing" />;
                        }
                        const isAnswer = isAnswerLine(text);
                        return (
                          <p {...props} className={isAnswer ? markdownStyles.answerLine : styles.markdownP}>
                            {isAnswer ? <span className={markdownStyles.answerLineInner}></span> : children}
                          </p>
                        );
                      },
                      strong: ({ node, ...props }) => (
                        <strong {...props} className={styles.markdownBold} />
                      ),
                      em: ({ node, ...props }) => (
                        <em {...props} className={styles.markdownItalic} />
                      ),
                      u: ({ node, ...props }) => (
                        <u {...props} className={styles.markdownUnderline} />
                      ),
                      img: ({ node, ...props }) => (
                        (() => {
                          const alignFromProps = props?.['data-align'] || props?.dataAlign || props?.align;
                          const alignFromNode = node?.properties?.['data-align'] || node?.properties?.dataAlign || node?.properties?.align;
                          const align = (alignFromProps || alignFromNode || 'left').toString().toLowerCase();
                          const style =
                            align === 'center'
                              ? { maxWidth: '100%', height: 'auto', display: 'block', marginLeft: 'auto', marginRight: 'auto' }
                              : align === 'right'
                                ? { maxWidth: '100%', height: 'auto', display: 'block', marginLeft: 'auto', marginRight: 0 }
                                : { maxWidth: '100%', height: 'auto', display: 'block', marginLeft: 0, marginRight: 'auto' };
                          return (
                            <img
                              {...props}
                              alt={props.alt || ''}
                              style={style}
                              loading="lazy"
                            />
                          );
                        })()
                      ),
                    }}
                  >
                    {sectionContentForDisplay}
                  </ReactMarkdown>
                  {editingIndex === null && (
                    <div className={styles.clickToEditHint}>
                      Click to edit this section
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
});

export default ContentEditor;
